import { useParams, useNavigate } from 'react-router-dom';
;
import { SEO } from '@/shared/SEO';
import {
  ArrowLeft, ArrowRight, Clock, User, ChevronRight, Sparkles,
  Twitter, Linkedin, Link2, Mail,
} from 'lucide-react';
import { getPostBySlug, getRelatedPosts, defaultCTA } from '../data/posts';

export default function BlogArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const post = slug ? getPostBySlug(slug) : null;

  if (!post) {
    return (
      <>
        <div className="mx-auto max-w-[68ch] py-32 text-center">
          <h1 className="text-4xl font-bold mb-4">Article not found</h1>
          <p className="text-muted-foreground mb-8">We couldn't find the article you're looking for.</p>
          <button
            onClick={() => navigate('/blog')}
            className="group inline-flex items-center gap-2 px-7 py-4 rounded-full bg-primary text-primary-foreground font-semibold text-sm shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/30 transition-all hover:-translate-y-0.5 cursor-pointer"
          >
            <ArrowLeft className="h-4 w-4" /> Back to blog
          </button>
        </div>
      </>
    );
  }

  const related = getRelatedPosts(post.slug, 3);
  const cta = post.cta || defaultCTA;
  const articleUrl = `https://bloomneo.com/blog/${post.slug}`;

  return (
    <>
      <SEO
        title={`${post.metaTitle ?? post.title} | Bloomneo Blog`}
        description={post.description}
        keywords={post.keywords}
      />

      {/* JSON-LD Article structured data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: post.title,
            description: post.description,
            author: { '@type': 'Organization', name: post.author },
            publisher: {
              '@type': 'Organization',
              name: 'Bloomneo',
              logo: { '@type': 'ImageObject', url: 'https://bloomneo.com/images/bloomneo_logo.svg' },
            },
            datePublished: post.date,
            mainEntityOfPage: { '@type': 'WebPage', '@id': articleUrl },
            articleSection: post.category,
            keywords: post.keywords,
          }),
        }}
      />

      {/* ━━━━━━━━━━━━━━━━ ARTICLE HERO ━━━━━━━━━━━━━━━━ */}
      <section className="relative">
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: 'linear-gradient(rgba(232,168,48,1) 1px, transparent 1px), linear-gradient(90deg, rgba(232,168,48,1) 1px, transparent 1px)',
            backgroundSize: '60px 60px',
            maskImage: 'radial-gradient(ellipse at center top, black 40%, transparent 75%)',
          }}
        />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full bg-primary/[0.05] blur-[150px] pointer-events-none" />

        <div className="relative pt-12 pb-12 md:pt-16 md:pb-16">
          {/* Back link */}
          <div className="bloom-rise">
            <button
              onClick={() => navigate('/blog')}
              className="group inline-flex items-center gap-2 text-muted-foreground hover:text-primary text-sm font-medium mb-8 cursor-pointer transition-colors"
            >
              <ArrowLeft className="h-4 w-4 group-hover:-translate-x-1 transition-transform" />
              Back to blog
            </button>
          </div>

          {/* Category */}
          <div className="bloom-rise">
            <div className="mb-6">
              <span className="text-[10px] font-semibold uppercase tracking-wider px-3 py-1 rounded-full bg-primary/10 text-primary border border-primary/20">
                {post.category}
              </span>
            </div>
          </div>

          {/* Title */}
          <div className="bloom-rise">
            <h1 className="font-bold tracking-[-0.03em] leading-[1.05] mb-6" style={{ fontSize: 'clamp(2rem, 5.5vw, 4rem)' }}>
              {post.title}
            </h1>
          </div>

          {/* Description */}
          <div className="bloom-rise">
            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed mb-8">
              {post.description}
            </p>
          </div>

          {/* Meta */}
          <div className="bloom-rise">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-3 pb-8 border-b border-border/40 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-primary" />
                <span>{post.author}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-primary" />
                <span>{post.readTime}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ━━━━━━━━━━━━━━━━ ARTICLE BODY ━━━━━━━━━━━━━━━━ */}
      <section className="relative">
        <div className="pb-12">

          {/* Hero quote */}
          <div className="bloom-rise">
            <div className="bg-gradient-to-br from-primary/10 via-primary/5 to-primary/10 border-l-4 border-primary rounded-r-2xl p-6 md:p-8 mb-12">
              <p className="text-lg md:text-xl font-semibold text-foreground leading-relaxed italic">
                "{post.hero}"
              </p>
            </div>
          </div>

          {/* Article content */}
          <div className="bloom-rise">
            <article className="prose prose-lg max-w-[72ch]
              [&>p]:text-base [&>p]:md:text-lg [&>p]:text-foreground/85 [&>p]:leading-[1.8] [&>p]:mb-6
              [&>h3]:text-2xl [&>h3]:md:text-3xl [&>h3]:font-bold [&>h3]:tracking-tight [&>h3]:text-foreground [&>h3]:mt-12 [&>h3]:mb-5
              [&>h4]:text-xl [&>h4]:md:text-2xl [&>h4]:font-bold [&>h4]:tracking-tight [&>h4]:text-foreground [&>h4]:mt-8 [&>h4]:mb-4
              [&>ul]:my-6 [&>ul]:space-y-3 [&>ul]:pl-5 [&>ul]:list-disc [&>ul]:marker:text-primary
              [&>ul>li]:text-base [&>ul>li]:md:text-lg [&>ul>li]:text-foreground/85 [&>ul>li]:leading-relaxed [&>ul>li]:pl-2
              [&_strong]:text-foreground [&_strong]:font-bold
              [&_em]:italic
            ">
              {post.body}
            </article>
          </div>

          {/* Share */}
          <div className="bloom-rise">
            <div className="mt-16 pt-8 border-t border-border/40">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <span className="text-sm font-semibold text-muted-foreground">Share this article</span>
                <div className="flex items-center gap-2">
                  <a
                    href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(articleUrl)}&text=${encodeURIComponent(post.title)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full border border-border/40 bg-muted/30 hover:border-primary/30 hover:bg-muted/60 flex items-center justify-center text-muted-foreground hover:text-primary transition-all cursor-pointer"
                    aria-label="Share on Twitter"
                  >
                    <Twitter className="h-4 w-4" />
                  </a>
                  <a
                    href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(articleUrl)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full border border-border/40 bg-muted/30 hover:border-primary/30 hover:bg-muted/60 flex items-center justify-center text-muted-foreground hover:text-primary transition-all cursor-pointer"
                    aria-label="Share on LinkedIn"
                  >
                    <Linkedin className="h-4 w-4" />
                  </a>
                  <a
                    href={`mailto:?subject=${encodeURIComponent(post.title)}&body=${encodeURIComponent(`${post.description}\n\n${articleUrl}`)}`}
                    className="w-10 h-10 rounded-full border border-border/40 bg-muted/30 hover:border-primary/30 hover:bg-muted/60 flex items-center justify-center text-muted-foreground hover:text-primary transition-all cursor-pointer"
                    aria-label="Share via Email"
                  >
                    <Mail className="h-4 w-4" />
                  </a>
                  <button
                    onClick={() => {
                      navigator.clipboard?.writeText(articleUrl);
                    }}
                    className="w-10 h-10 rounded-full border border-border/40 bg-muted/30 hover:border-primary/30 hover:bg-muted/60 flex items-center justify-center text-muted-foreground hover:text-primary transition-all cursor-pointer"
                    aria-label="Copy link"
                  >
                    <Link2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ━━━━━━━━━━━━━━━━ LEAD MAGNET CTA ━━━━━━━━━━━━━━━━ */}
      <section className="pb-20">
        <div className="bloom-rise">
          <div className="bg-gradient-to-br from-primary/15 via-primary/5 to-primary/10 border border-primary/30 rounded-3xl p-8 md:p-12">
            <div className="flex items-start gap-5 mb-6">
              <div className="w-14 h-14 rounded-2xl bg-primary/15 border border-primary/30 flex items-center justify-center flex-shrink-0">
                <Sparkles className="h-6 w-6 text-primary" />
              </div>
              <div className="flex-1">
                <h3 className="text-2xl md:text-3xl font-bold tracking-tight mb-3">{cta.title}</h3>
                <p className="text-base text-muted-foreground leading-relaxed">{cta.description}</p>
              </div>
            </div>
            <button
              onClick={() => navigate(cta.href)}
              className="group inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-primary text-primary-foreground font-semibold text-sm shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/30 transition-all hover:-translate-y-0.5 cursor-pointer"
            >
              {cta.buttonText}
              <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>
      </section>

      {/* ━━━━━━━━━━━━━━━━ RELATED ━━━━━━━━━━━━━━━━ */}
      <section className="bg-muted/30 border-y border-border/40">
        <div className="py-20 md:py-28">
          <div className="bloom-rise">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2.5 bg-primary/10 border border-primary/20 rounded-full px-4 py-1.5 mb-4">
                <span className="text-[11px] font-semibold tracking-[0.12em] uppercase text-primary">Keep reading</span>
              </div>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-[-0.03em] leading-[0.95]">
                More from the blog.
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            {related.map((rp, i) => (
              <div key={rp.slug} className={`bloom-rise bloom-rise-${Math.min(i + 1, 4)}`}>
                <div>
                  <button
                    onClick={() => navigate(`/blog/${rp.slug}`)}
                    className="group h-full w-full text-left bg-background border border-border/40 rounded-2xl p-7 hover:border-primary/30 transition-all cursor-pointer flex flex-col"
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <span className="text-[10px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full bg-primary/10 text-primary border border-primary/20">
                        {rp.category}
                      </span>
                      <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                        <Clock className="h-3 w-3" /> {rp.readTime}
                      </span>
                    </div>
                    <h3 className="text-lg font-bold tracking-tight leading-tight mb-3 group-hover:text-primary transition-colors">
                      {rp.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed mb-5 flex-1 line-clamp-3">
                      {rp.description}
                    </p>
                    <div className="flex items-center gap-1 text-primary font-semibold text-xs">
                      Read more
                      <ArrowRight className="h-3 w-3 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-10">
            <button
              onClick={() => navigate('/blog')}
              className="group inline-flex items-center gap-2 text-primary font-semibold text-sm hover:gap-3 transition-all cursor-pointer"
            >
              See all articles <ChevronRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>
      </section>
    </>
  );
}
